import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { bookingService } from '../services/bookingService';
import { sectionService } from '../services/sectionService';
import './MyBookingsPage.css';

// ========== BEAUTIFUL LOADING COMPONENT ==========
const LoadingSpinner = () => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95) {
          clearInterval(interval);
          return 95;
        }
        return prev + Math.random() * 15;
      });
    }, 200);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="loading-container">
      <div className="loading-card">
        <div className="loading-spinner">
          <div className="spinner-ring"></div>
          <div className="spinner-ring-inner"></div>
          <i className="fa-solid fa-calendar-check loading-icon"></i>
        </div>
        
        <div className="loading-text">
          <h3>Loading Your Bookings</h3>
          <p>Please wait while we fetch your data...</p>
        </div>
        
        <div className="progress-bar-container">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${Math.min(progress, 95)}%` }}
          ></div>
          <span className="progress-percentage">{Math.floor(Math.min(progress, 95))}%</span>
        </div>
        
        <div className="loading-tips">
          <div className="tip-item">
            <i className="fa-regular fa-lightbulb"></i>
            <span>Managing your resources efficiently</span>
          </div>
          <div className="tip-item">
            <i className="fa-regular fa-clock"></i>
            <span>This won't take long</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const MyBookingsPage = ({ 
  user = null, 
  submitFeedback = () => {},
  addNotification = () => {},
  addAuditLog = () => {} 
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('facilities');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  
  // Reconciliation states
  const [showReconciliationModal, setShowReconciliationModal] = useState(false);
  const [reconcilingBooking, setReconcilingBooking] = useState(null);
  const [bookingItems, setBookingItems] = useState([]);
  const [selectedReturnItems, setSelectedReturnItems] = useState([]);
  const [returnConditionNotes, setReturnConditionNotes] = useState('');
  const [returningItems, setReturningItems] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  
  // Data states
  const [facilityBookings, setFacilityBookings] = useState([]);
  const [cancelledBookings, setCancelledBookings] = useState([]);
  const [sectionMemberships, setSectionMemberships] = useState([]);
  const [equipmentRequests, setEquipmentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to safely parse equipment_items (JSON string or array)
  const parseEquipmentItems = (items) => {
    if (!items) return [];
    if (Array.isArray(items)) return items;
    if (typeof items === 'string') {
      try {
        const parsed = JSON.parse(items);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('Failed to parse equipment items:', e);
        return [];
      }
    }
    return [];
  };

  // Helper function to check if booking is cancelled
  const isCancelled = (booking) => {
    return booking?.status === 'cancelled';
  };

  // Fetch all data
  const fetchAllData = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('🔄 FETCHING FRESH DATA FROM API...', new Date().toLocaleTimeString());
      
      const [bookingsResponse, membershipsResponse] = await Promise.all([
        bookingService.getUserBookings(),
        sectionService.getMyMemberships()
      ]);
      
      console.log('📡 Bookings response:', bookingsResponse);
      console.log('📡 Section memberships response:', membershipsResponse);
      
      if (bookingsResponse.success && bookingsResponse.bookings) {
        const allFacilities = bookingsResponse.bookings.filter(b => b.booking_type === 'facility');
        const equipment = bookingsResponse.bookings.filter(b => b.booking_type === 'equipment');
        
        const activeFacilities = allFacilities.filter(b => !isCancelled(b));
        const cancelledFacilities = allFacilities.filter(b => isCancelled(b));
        
        const sortedActive = [...activeFacilities].sort((a, b) => 
          new Date(b.created_at || b.booking_date) - new Date(a.created_at || a.booking_date)
        );
        const sortedCancelled = [...cancelledFacilities].sort((a, b) => 
          new Date(b.created_at || b.booking_date) - new Date(a.created_at || a.booking_date)
        );
        const sortedEquipment = [...equipment].sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        
        setFacilityBookings(sortedActive);
        setCancelledBookings(sortedCancelled);
        setEquipmentRequests(sortedEquipment);
      }
      
      if (membershipsResponse.success && membershipsResponse.memberships) {
        const sortedMemberships = [...membershipsResponse.memberships].sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setSectionMemberships(sortedMemberships);
      }
      
      setError(null);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Network error. Please try again later.');
      addNotification('Network error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load booking items for reconciliation
  const loadBookingItems = async (booking) => {
    try {
      const response = await bookingService.getBookingItems(booking.id);
      if (response.success && response.items && response.items.length > 0) {
        setBookingItems(response.items);
        const issuedItems = response.items
          .filter(item => item.status === 'issued')
          .map(item => item.id);
        setSelectedReturnItems(issuedItems);
        setReconcilingBooking(booking);
        setShowReconciliationModal(true);
      } else if (response.items && response.items.length === 0) {
        alert('No items to return for this booking. All items have been returned already.');
      } else {
        alert('No items found for this booking.');
      }
    } catch (error) {
      console.error('Error loading booking items:', error);
      alert('Failed to load booking items. Please try again.');
    }
  };

  // Handle return items
  const handleReturnItems = async () => {
    if (selectedReturnItems.length === 0) {
      alert('Please select at least one item to return');
      return;
    }
    
    setReturningItems(true);
    try {
      const response = await bookingService.returnItems(
        reconcilingBooking.id,
        selectedReturnItems,
        returnConditionNotes
      );
      
      if (response.success) {
        addNotification(`✅ ${response.returned_count} item(s) returned successfully!`, 'success');
        setShowReconciliationModal(false);
        setReconcilingBooking(null);
        setBookingItems([]);
        setSelectedReturnItems([]);
        setReturnConditionNotes('');
        await fetchAllData();
      } else {
        addNotification(response.message || 'Failed to return items', 'error');
      }
    } catch (error) {
      console.error('Error returning items:', error);
      addNotification('An error occurred. Please try again.', 'error');
    } finally {
      setReturningItems(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchAllData();
  }, [user?.id]);

  // Reset to first page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Get current data for pagination based on active tab
  const getCurrentData = () => {
    let data = [];
    switch(activeTab) {
      case 'facilities':
        data = facilityBookings;
        break;
      case 'cancelled':
        data = cancelledBookings;
        break;
      case 'sections':
        data = sectionMemberships;
        break;
      case 'equipment':
        data = equipmentRequests;
        break;
      default:
        data = [];
    }
    
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(data.length / itemsPerPage);
    
    return { currentItems, totalPages, totalItems: data.length };
  };

  const { currentItems, totalPages, totalItems } = getCurrentData();

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const gridElement = document.querySelector('.bookings-grid');
    if (gridElement) {
      gridElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Handle cancel booking
  const handleCancelBooking = async (item) => {
    const reason = window.prompt('Please provide a reason for cancellation:', 'User requested cancellation');
    
    if (window.confirm(`Are you sure you want to cancel ${item.facility_name || 'this booking'}? This will make the facility available for others.`)) {
      try {
        setCancellingId(item.id);
        
        const response = await bookingService.cancelBooking(item.id);
        
        if (response && response.success) {
          addNotification('Booking cancelled successfully. Facility is now available!', 'success');
          
          if (addAuditLog) {
            addAuditLog('Booking Cancelled (Deleted)', { 
              bookingId: item.id, 
              reason: reason || 'No reason provided'
            });
          }
          
          setFacilityBookings(prev => prev.filter(b => b.id !== item.id));
          await fetchAllData();
          
        } else {
          addNotification(response?.message || 'Failed to cancel booking', 'error');
        }
      } catch (err) {
        console.error('Cancel booking error:', err);
        addNotification('An error occurred. Please try again.', 'error');
      } finally {
        setCancellingId(null);
      }
    }
  };

  // Handle cancel section membership
  const handleCancelSectionMembership = async (membership) => {
    if (window.confirm(`Are you sure you want to cancel your request to join ${membership.section_name}?`)) {
      try {
        const response = await sectionService.cancelMembership(membership.id);
        
        if (response.success) {
          addNotification('Section membership request cancelled successfully', 'success');
          if (addAuditLog) addAuditLog('Section Membership Cancelled', { 
            membershipId: membership.id, 
            section: membership.section_name 
          });
          
          const refreshedResponse = await sectionService.getMyMemberships();
          if (refreshedResponse.success && refreshedResponse.memberships) {
            const sortedMemberships = [...refreshedResponse.memberships].sort((a, b) => 
              new Date(b.created_at) - new Date(a.created_at)
            );
            setSectionMemberships(sortedMemberships);
          }
        } else {
          addNotification(response.message || 'Failed to cancel request', 'error');
        }
      } catch (err) {
        console.error('Cancel membership error:', err);
        addNotification('An error occurred. Please try again.', 'error');
      }
    }
  };

  const handleFeedbackSubmit = () => {
    if (selectedBooking) {
      submitFeedback(selectedBooking.id, feedbackRating, feedbackComment);
      setShowFeedbackModal(false);
      setSelectedBooking(null);
      setFeedbackRating(5);
      setFeedbackComment('');
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return 'fa-solid fa-check-circle';
      case 'pending': return 'fa-solid fa-clock';
      case 'rejected': return 'fa-solid fa-times-circle';
      case 'cancelled': return 'fa-solid fa-ban';
      case 'waitlist': return 'fa-solid fa-hourglass-half';
      case 'location_changed': return 'fa-solid fa-location-dot';
      default: return 'fa-solid fa-circle';
    }
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'approved': return 'status-approved';
      case 'pending': return 'status-pending';
      case 'rejected': return 'status-rejected';
      case 'cancelled': return 'status-cancelled';
      case 'waitlist': return 'status-waitlist';
      case 'location_changed': return 'status-location-changed';
      default: return '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  // Calculate stats for each tab
  const facilityStats = {
    total: facilityBookings.length,
    pending: facilityBookings.filter(b => b?.status === 'pending').length,
    approved: facilityBookings.filter(b => b?.status === 'approved').length,
    rejected: facilityBookings.filter(b => b?.status === 'rejected').length,
    cancelled: cancelledBookings.length
  };
  
  const sectionStats = {
    total: sectionMemberships.length,
    pending: sectionMemberships.filter(m => m?.status === 'pending').length,
    approved: sectionMemberships.filter(m => m?.status === 'approved').length,
    rejected: sectionMemberships.filter(m => m?.status === 'rejected').length
  };
  
  const equipmentStats = {
    total: equipmentRequests.length,
    pending: equipmentRequests.filter(e => e?.status === 'pending').length,
    approved: equipmentRequests.filter(e => e?.status === 'approved').length,
    rejected: equipmentRequests.filter(e => e?.status === 'rejected').length
  };

  // Render a single card
  const renderBookingCard = (item, type) => {
    const isFacility = type === 'facility';
    const isSection = type === 'section';
    const isEquipment = type === 'equipment';
    const isCancelling = cancellingId === item.id;
    const isCancelledStatus = isCancelled(item);
    
    // Parse equipment items safely
    const equipmentItems = parseEquipmentItems(item?.equipment_items);
    
    if (isFacility) {
      return (
        <motion.div
          key={item.id}
          className={`booking-card ${getStatusClass(item?.status)}`}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          whileHover={{ y: -4 }}
        >
          <div className="booking-header">
            <div className="booking-type-icon">
              <i className="fa-solid fa-building"></i>
            </div>
            <div className="booking-title">
              <h3>{item?.facility_name}</h3>
              <span className="booking-id">#{item?.booking_number}</span>
            </div>
            <div className={`booking-status ${item?.status}`}>
              <i className={getStatusIcon(item?.status)}></i>
              <span>{item?.status?.charAt(0).toUpperCase() + item?.status?.slice(1)}</span>
            </div>
          </div>

          <div className="booking-details">
            <div className="detail-row">
              <i className="fa-regular fa-calendar"></i>
              <span>{formatDate(item?.booking_date)}</span>
            </div>
            <div className="detail-row">
              <i className="fa-regular fa-clock"></i>
              <span>{formatTime(item?.start_time)} - {formatTime(item?.end_time)}</span>
            </div>
            <div className="detail-row">
              <i className="fa-solid fa-location-dot"></i>
              <span>{item?.location}</span>
            </div>
            <div className="detail-row">
              <i className="fa-solid fa-users"></i>
              <span>{item?.guests || item?.guests_count} guests</span>
            </div>
            <div className="detail-row">
              <i className="fa-solid fa-tag"></i>
              <span className="event-type">{item?.event_type || 'Private'}</span>
            </div>
            
            {/* Equipment items for facility booking - FIXED */}
            {equipmentItems.length > 0 && (
              <div className="equipment-items">
                <strong>Equipment Requested:</strong>
                <div className="items-list">
                  {equipmentItems.map((eq, index) => (
                    <span key={index} className="item-tag">
                      <i className="fa-solid fa-toolbox"></i>
                      {eq.name} x{eq.quantity || 1}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {item?.notes && (
              <div className="booking-notes">
                <i className="fa-solid fa-note-sticky"></i>
                <p>{item?.notes}</p>
              </div>
            )}
            
            {item?.rejection_reason && (
              <div className="rejection-reason">
                <i className="fa-solid fa-exclamation-circle"></i>
                <p><strong>Reason:</strong> {item?.rejection_reason}</p>
              </div>
            )}
            
            {isCancelledStatus && item?.cancelled_at && (
              <div className="cancellation-reason">
                <i className="fa-solid fa-info-circle"></i>
                <p>Cancelled on {formatDate(item?.cancelled_at)}</p>
              </div>
            )}
          </div>

          <div className="booking-footer">
            <span className="booking-date">
              Booked on {formatDate(item?.created_at)}
            </span>
            
            <div className="booking-actions">
              {item?.status === 'pending' && !isCancelledStatus && (
                <button 
                  className="action-btn cancel-btn"
                  onClick={() => handleCancelBooking(item)}
                  disabled={isCancelling}
                >
                  {isCancelling ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin"></i> Cancelling...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-times"></i> Cancel
                    </>
                  )}
                </button>
              )}
              {item?.status === 'approved' && !isCancelledStatus && (
                <>
                  <button 
                    className="action-btn view-btn"
                    onClick={() => navigate(`/booking/${item?.id}`)}
                  >
                    <i className="fa-solid fa-eye"></i> Details
                  </button>
                  <button 
                    className="action-btn reconcile-btn"
                    onClick={() => loadBookingItems(item)}
                  >
                    <i className="fa-solid fa-rotate-left"></i> Return Items
                  </button>
                </>
              )}
              {isCancelledStatus && (
                <span className="cancelled-badge">
                  <i className="fa-solid fa-ban"></i> Cancelled
                </span>
              )}
            </div>
          </div>
        </motion.div>
      );
    }
    
    // Section rendering
    if (isSection) {
      return (
        <motion.div
          key={item.id}
          className={`booking-card ${getStatusClass(item?.status)}`}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          whileHover={{ y: -4 }}
        >
          <div className="booking-header">
            <div className="booking-type-icon">
              <i className="fa-solid fa-id-card"></i>
            </div>
            <div className="booking-title">
              <h3>{item?.section_name}</h3>
              <span className="booking-id">#{item?.id}</span>
            </div>
            <div className={`booking-status ${item?.status}`}>
              <i className={getStatusIcon(item?.status)}></i>
              <span>{item?.status?.charAt(0).toUpperCase() + item?.status?.slice(1)}</span>
            </div>
          </div>

          <div className="booking-details">
            <div className="detail-row">
              <i className="fa-regular fa-calendar"></i>
              <span>Requested on {formatDate(item?.created_at)}</span>
            </div>
            
            {item?.jersey_request?.need && (
              <div className="detail-row">
                <i className="fa-solid fa-shirt"></i>
                <span>Jersey: {item?.jersey_request?.size} ({item?.jersey_request?.type})</span>
              </div>
            )}
            
            {item?.equipment_requests && item.equipment_requests.length > 0 && (
              <div className="equipment-items">
                <strong>Equipment Requests:</strong>
                <div className="items-list">
                  {item.equipment_requests.map((eq, index) => (
                    <span key={index} className="item-tag">
                      <i className="fa-solid fa-toolbox"></i>
                      {eq.name} ({eq.type})
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {item?.notes && (
              <div className="booking-notes">
                <i className="fa-solid fa-note-sticky"></i>
                <p>{item?.notes}</p>
              </div>
            )}
            
            {item?.rejection_reason && (
              <div className="rejection-reason">
                <i className="fa-solid fa-exclamation-circle"></i>
                <p><strong>Reason:</strong> {item?.rejection_reason}</p>
              </div>
            )}
          </div>

          <div className="booking-footer">
            <span className="booking-date">
              {item?.status === 'pending' ? 'Awaiting approval' : 
               item?.status === 'approved' ? 'Approved' : 
               item?.status === 'rejected' ? 'Rejected' : 'Requested'}
            </span>
            
            <div className="booking-actions">
              {item?.status === 'pending' && (
                <button 
                  className="action-btn cancel-btn"
                  onClick={() => handleCancelSectionMembership(item)}
                >
                  <i className="fa-solid fa-times"></i> Cancel Request
                </button>
              )}
              {item?.status === 'approved' && (
                <span className="membership-note success">
                  <i className="fa-solid fa-check-circle"></i> Active Member
                </span>
              )}
              {item?.status === 'rejected' && (
                <button 
                  className="action-btn view-btn"
                  onClick={() => navigate('/sections')}
                >
                  <i className="fa-solid fa-arrow-right"></i> Apply Again
                </button>
              )}
            </div>
          </div>
        </motion.div>
      );
    }
    
    // Equipment rendering - FIXED with safe parsing
    if (isEquipment) {
      return (
        <motion.div
          key={item.id}
          className={`booking-card ${getStatusClass(item?.status)}`}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          whileHover={{ y: -4 }}
        >
          <div className="booking-header">
            <div className="booking-type-icon">
              <i className="fa-solid fa-toolbox"></i>
            </div>
            <div className="booking-title">
              <h3>Equipment Request</h3>
              <span className="booking-id">#{item?.booking_number}</span>
            </div>
            <div className={`booking-status ${item?.status}`}>
              <i className={getStatusIcon(item?.status)}></i>
              <span>{item?.status?.charAt(0).toUpperCase() + item?.status?.slice(1)}</span>
            </div>
          </div>

          <div className="booking-details">
            <div className="detail-row">
              <i className="fa-solid fa-clipboard-list"></i>
              <span>{item?.purpose || 'No purpose specified'}</span>
            </div>
            <div className="detail-row">
              <i className="fa-regular fa-clock"></i>
              <span>Return within {item?.borrow_duration || 24} hours</span>
            </div>
            
            {/* Equipment items for equipment request - FIXED */}
            {equipmentItems.length > 0 && (
              <div className="equipment-items">
                <strong>Items:</strong>
                <div className="items-list">
                  {equipmentItems.map((eq, index) => (
                    <span key={index} className="item-tag">
                      <i className="fa-solid fa-toolbox"></i>
                      {eq.name} x{eq.quantity || 1}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {item?.notes && (
              <div className="booking-notes">
                <i className="fa-solid fa-note-sticky"></i>
                <p>{item?.notes}</p>
              </div>
            )}
          </div>

          <div className="booking-footer">
            <span className="booking-date">
              Requested on {formatDate(item?.created_at)}
            </span>
            
            <div className="booking-actions">
              {item?.status === 'pending' && (
                <button 
                  className="action-btn cancel-btn"
                  onClick={() => handleCancelBooking(item)}
                  disabled={isCancelling}
                >
                  {isCancelling ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin"></i> Cancelling...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-times"></i> Cancel
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      );
    }
    
    return null;
  };

  // Pagination component
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      
      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) pages.push(i);
          pages.push('...');
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push('...');
          for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(totalPages);
        }
      }
      return pages;
    };
    
    return (
      <div className="pagination-container">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <i className="fa-solid fa-chevron-left"></i> Previous
        </button>
        
        <div className="pagination-numbers">
          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              className={`pagination-number ${page === currentPage ? 'active' : ''} ${page === '...' ? 'dots' : ''}`}
              onClick={() => page !== '...' && onPageChange(page)}
              disabled={page === '...'}
            >
              {page}
            </button>
          ))}
        </div>
        
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next <i className="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="my-bookings-page">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="page-title">
            <i className="fa-solid fa-calendar-check"></i>
            My Bookings
          </h2>
          <button 
            onClick={fetchAllData}
            className="refresh-btn"
            style={{
              padding: '8px 16px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#e6f2ea';
              e.target.style.borderColor = '#00843D';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'white';
              e.target.style.borderColor = '#e5e7eb';
            }}
          >
            <i className="fa-solid fa-rotate-right"></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-btn ${activeTab === 'facilities' ? 'active' : ''}`}
          onClick={() => setActiveTab('facilities')}
        >
          <i className="fa-solid fa-building"></i>
          Facilities
          <span className="tab-count">{facilityStats.total}</span>
        </button>
        <button 
          className={`tab-btn ${activeTab === 'cancelled' ? 'active' : ''}`}
          onClick={() => setActiveTab('cancelled')}
        >
          <i className="fa-solid fa-ban"></i>
          Cancelled
          <span className="tab-count">{facilityStats.cancelled}</span>
        </button>
        <button 
          className={`tab-btn ${activeTab === 'sections' ? 'active' : ''}`}
          onClick={() => setActiveTab('sections')}
        >
          <i className="fa-solid fa-id-card"></i>
          Sections
          <span className="tab-count">{sectionStats.total}</span>
        </button>
        <button 
          className={`tab-btn ${activeTab === 'equipment' ? 'active' : ''}`}
          onClick={() => setActiveTab('equipment')}
        >
          <i className="fa-solid fa-toolbox"></i>
          Equipment
          <span className="tab-count">{equipmentStats.total}</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon total">
            <i className="fa-solid fa-calendar"></i>
          </div>
          <div className="stat-info">
            <span className="stat-value">
              {activeTab === 'facilities' ? facilityStats.total :
               activeTab === 'cancelled' ? facilityStats.cancelled :
               activeTab === 'sections' ? sectionStats.total :
               equipmentStats.total}
            </span>
            <span className="stat-label">Total</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon pending">
            <i className="fa-solid fa-clock"></i>
          </div>
          <div className="stat-info">
            <span className="stat-value">
              {activeTab === 'facilities' ? facilityStats.pending :
               activeTab === 'sections' ? sectionStats.pending :
               equipmentStats.pending}
            </span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon approved">
            <i className="fa-solid fa-check-circle"></i>
          </div>
          <div className="stat-info">
            <span className="stat-value">
              {activeTab === 'facilities' ? facilityStats.approved :
               activeTab === 'sections' ? sectionStats.approved :
               equipmentStats.approved}
            </span>
            <span className="stat-label">Approved</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon rejected">
            <i className="fa-solid fa-times-circle"></i>
          </div>
          <div className="stat-info">
            <span className="stat-value">
              {activeTab === 'facilities' ? facilityStats.rejected :
               activeTab === 'sections' ? sectionStats.rejected :
               equipmentStats.rejected}
            </span>
            <span className="stat-label">Rejected</span>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="tab-content"
        >
          {totalItems === 0 ? (
            <div className="empty-state">
              <i className={`fa-solid ${activeTab === 'facilities' ? 'fa-building' : activeTab === 'cancelled' ? 'fa-ban' : activeTab === 'sections' ? 'fa-users' : 'fa-toolbox'} empty-icon`}></i>
              <h3>
                {activeTab === 'facilities' && 'No Active Facility Bookings'}
                {activeTab === 'cancelled' && 'No Cancelled Bookings'}
                {activeTab === 'sections' && 'No Section Memberships'}
                {activeTab === 'equipment' && 'No Equipment Requests'}
              </h3>
              <p>
                {activeTab === 'facilities' && "You don't have any active facility bookings"}
                {activeTab === 'cancelled' && "You haven't cancelled any bookings"}
                {activeTab === 'sections' && "You haven't joined any sections yet"}
                {activeTab === 'equipment' && "You haven't requested any equipment yet"}
              </p>
              {activeTab !== 'cancelled' && (
                <button 
                  className="btn-primary"
                  onClick={() => navigate(activeTab === 'facilities' ? '/facilities' : activeTab === 'sections' ? '/sections' : '/equipment')}
                >
                  <i className={`fa-solid ${activeTab === 'facilities' ? 'fa-building' : activeTab === 'sections' ? 'fa-users' : 'fa-toolbox'}`}></i>
                  {activeTab === 'facilities' && ' Browse Facilities'}
                  {activeTab === 'sections' && ' Join a Section'}
                  {activeTab === 'equipment' && ' Request Equipment'}
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="bookings-grid">
                {currentItems.map(item => {
                  if (activeTab === 'facilities') return renderBookingCard(item, 'facility');
                  if (activeTab === 'cancelled') return renderBookingCard(item, 'facility');
                  if (activeTab === 'sections') return renderBookingCard(item, 'section');
                  if (activeTab === 'equipment') return renderBookingCard(item, 'equipment');
                  return null;
                })}
              </div>
              
              <Pagination 
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
              
              <div className="pagination-info">
                Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Reconciliation Modal */}
      {showReconciliationModal && reconcilingBooking && (
        <div className="modal-overlay" onClick={() => setShowReconciliationModal(false)}>
          <div className="reconciliation-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-content">
                <div className="modal-icon">
                  <i className="fa-solid fa-rotate-left"></i>
                </div>
                <h3>Return Items</h3>
              </div>
              <button className="modal-close" onClick={() => setShowReconciliationModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="booking-info">
                <div className="info-row">
                  <span className="info-label">Booking Reference:</span>
                  <span className="info-value">{reconcilingBooking?.booking_number}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Facility:</span>
                  <span className="info-value">{reconcilingBooking?.facility_name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Date:</span>
                  <span className="info-value">{formatDate(reconcilingBooking?.booking_date)}</span>
                </div>
              </div>
              
              <div className="items-section">
                <h4>
                  <i className="fa-solid fa-boxes"></i>
                  Items to Return
                </h4>
                
                {bookingItems.length === 0 ? (
                  <div className="empty-items">
                    <i className="fa-solid fa-check-circle"></i>
                    <p>No items to return. All items have been returned!</p>
                  </div>
                ) : (
                  <div className="items-list">
                    {bookingItems.map(item => (
                      <div key={item.id} className="item-card">
                        <label className="item-checkbox">
                          <input 
                            type="checkbox"
                            checked={selectedReturnItems.includes(item.id)}
                            onChange={() => {
                              if (selectedReturnItems.includes(item.id)) {
                                setSelectedReturnItems(selectedReturnItems.filter(id => id !== item.id));
                              } else {
                                setSelectedReturnItems([...selectedReturnItems, item.id]);
                              }
                            }}
                            disabled={item.status !== 'issued'}
                          />
                          <div className="item-info">
                            <div className="item-icon">
                              {item.type === 'equipment' ? (
                                <i className="fa-solid fa-toolbox"></i>
                              ) : (
                                <i className="fa-solid fa-cube"></i>
                              )}
                            </div>
                            <div className="item-details">
                              <span className="item-name">{item.display_name || item.item_name}</span>
                              <span className="item-quantity">Quantity: {item.quantity}</span>
                            </div>
                          </div>
                        </label>
                        <div className={`item-status status-${item.status}`}>
                          {item.status === 'issued' && <i className="fa-solid fa-check-circle"></i>}
                          {item.status === 'returned' && <i className="fa-solid fa-check-double"></i>}
                          {item.status === 'pending' && <i className="fa-solid fa-clock"></i>}
                          <span>{item.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {bookingItems.filter(i => i.status === 'issued').length > 0 && (
                  <div className="condition-section">
                    <label>
                      <i className="fa-solid fa-clipboard-list"></i>
                      Condition Notes (Optional)
                    </label>
                    <textarea 
                      rows="3"
                      placeholder="Describe the condition of the items being returned..."
                      value={returnConditionNotes}
                      onChange={(e) => setReturnConditionNotes(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => setShowReconciliationModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-return"
                onClick={handleReturnItems}
                disabled={returningItems || selectedReturnItems.length === 0}
              >
                {returningItems ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    Returning...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-rotate-left"></i>
                    Return Selected ({selectedReturnItems.length})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && selectedBooking && (
        <div className="modal-overlay" onClick={() => setShowFeedbackModal(false)}>
          <div className="feedback-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fa-solid fa-star"></i>
                Leave Feedback
              </h3>
              <button className="close-btn" onClick={() => setShowFeedbackModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <p className="booking-ref">
                Booking #{selectedBooking?.booking_number || selectedBooking?.id}
              </p>
              
              <div className="rating-section">
                <label>Your Rating</label>
                <div className="stars">
                  {[1, 2, 3, 4, 5].map(star => (
                    <i
                      key={star}
                      className={`fa-solid fa-star ${star <= feedbackRating ? 'active' : ''}`}
                      onClick={() => setFeedbackRating(star)}
                    ></i>
                  ))}
                </div>
              </div>
              
              <div className="form-group">
                <label>Your Comments</label>
                <textarea
                  rows="4"
                  placeholder="Tell us about your experience..."
                  value={feedbackComment}
                  onChange={(e) => setFeedbackComment(e.target.value)}
                ></textarea>
              </div>
              
              <div className="modal-footer">
                <button 
                  className="btn-secondary"
                  onClick={() => setShowFeedbackModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleFeedbackSubmit}
                >
                  Submit Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBookingsPage;